import { useEffect } from 'react';
import type { To } from 'react-router';
import { useMatch } from 'react-router';

import { useFeatureFlags } from './useFeatureFlags.tsx';
import { STORAGE, useInMemoryStorage } from './useInMemoryStorage.ts';
import { LIFECYCLE, useLifecycle } from './useLifecycle.ts';

export const useContextNavigation = ({ to }: { to?: To }) => {
  const {
    flags: { useContextModeNavigation = false },
  } = useFeatureFlags();
  const [savedHistory, setSavedHistory] = useInMemoryStorage<
    Record<string, string>
  >(STORAGE.savedHistory, {});
  const canBeSaved = typeof to === 'string';
  const enabledContextModeNavigation = useContextModeNavigation && canBeSaved;
  const { pathname } = useMatch(`${to}/*`) || {};
  const savedItem = canBeSaved && savedHistory?.[to];

  useEffect(() => {
    if (enabledContextModeNavigation && pathname && pathname !== savedItem) {
      setSavedHistory(saved => ({ ...saved, [to]: pathname }));
    }
  });

  if (enabledContextModeNavigation && !pathname) {
    return savedItem;
  }
  return undefined;
};

/** Similar to context mode navigation but only for the first subpath level (tabs)
 *  and is stored in the user's lifecycle instead of in-memory
 **/
export const useStoredContextNavigation = ({ to }: { to?: To }) => {
  const {
    flags: { useStoredLastNavigation = false },
  } = useFeatureFlags();
  const { lifecycle, update } = useLifecycle();
  const canBeSaved = typeof to === 'string';
  const enabledStoredLastNavigation = useStoredLastNavigation && canBeSaved;
  const { params } = useMatch(`${to}/:subpath/*`) || {};
  const pathname = params?.subpath && `${to}/${params?.subpath}`;
  const savedItem =
    canBeSaved && lifecycle?.[LIFECYCLE.storedLastNavigation]?.[to];

  useEffect(() => {
    if (enabledStoredLastNavigation && pathname && pathname !== savedItem) {
      update(LIFECYCLE.storedLastNavigation, {
        ...lifecycle?.[LIFECYCLE.storedLastNavigation],
        [to]: pathname,
      });
    }
  });

  if (enabledStoredLastNavigation && !pathname) {
    return savedItem;
  }
  return undefined;
};
